import { Loader2, Users } from "lucide-react";
import { translateWorkerNote } from "./workerNote";
import { projectName } from "./projectText";
import {
  useGetList,
  useLocaleState,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company, CompanyTeamLookup, ProjectCompanyLink } from "../types";
import { useCreditPrices } from "./creditPrices";
import { PhaseBadge } from "./PhaseBadge";
import { countryName, formatDay, roleKey } from "./projectUtils";
import { useInvited } from "./useInvited";

/** Projets où la société est citée, avec le rôle qu'elle y tient. */
export const CompanyProjects = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const company = useRecordContext<Company>();
  const { data: links } = useGetList<ProjectCompanyLink>(
    "project_company_links",
    {
      filter: { company_id: company?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "aegyra_project_id", order: "DESC" },
    },
    { enabled: !!company },
  );
  if (!links?.length) return null;

  return (
    <Card className="mt-4">
      <CardContent>
        <h3 className="text-sm font-bold mb-4">
          {translate("resources.projects.company_projects", {
            smart_count: links.length,
          })}
        </h3>
        <ul className="flex flex-col gap-3">
          {links.map((link) => (
            <li
              key={link.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
            >
              <Link
                to={`/projects/${link.project_id}/show`}
                className="w-full font-medium underline"
              >
                {projectName(
                  { name: link.project_name, name_fr: link.project_name_fr },
                  locale,
                )}
              </Link>
              <span className="text-muted-foreground">
                {[
                  translate(roleKey(link.role), { _: link.role }),
                  countryName(link.project_country, locale),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              <PhaseBadge phase={link.project_phase} />
            </li>
          ))}
        </ul>
        <CompanyTeam company={company!} links={links} />
      </CardContent>
    </Card>
  );
};

/** « Chercher l'équipe » depuis la fiche entreprise : la demande porte sur la fiche la plus récente où la société
 * bâtit (pas comme bailleur), car le cloisonnement et le débit passent par une fiche ; les personnes trouvées
 * rejoignent les contacts de la société et cette fiche. */
const CompanyTeam = ({
  company,
  links,
}: {
  company: Company;
  links: ProjectCompanyLink[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const invited = useInvited();
  const prices = useCreditPrices();
  const [busy, setBusy] = useState(false);
  const target = links.find((link) => link.role !== "funder");
  const { data: lookups, refetch } = useGetList<CompanyTeamLookup>(
    "company_team_lookups",
    {
      filter: { company_id: company.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: !invited && !!target },
  );
  const latest = lookups?.[0];
  const pending = latest?.status === "pending";
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => refetch(), 5000);
    return () => clearInterval(timer);
  }, [pending, refetch]);
  if (invited || !target) return null;
  const price = prices?.team ?? 0;
  const ask = async () => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("request_company_team", {
      project: target.project_id,
      company: company.id,
    });
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.contacts.team.requested", { type: "info" });
    refetch();
  };
  return (
    <div className="mt-4 flex flex-col gap-2 border-t pt-4">
      <h4 className="text-xs font-bold">
        {translate("resources.projects.contacts.team.company_title")}
      </h4>
      <p className="text-xs text-muted-foreground">
        {translate("resources.projects.contacts.team.help", {
          smart_count: price,
        })}{" "}
        {translate("resources.projects.contacts.team.from_company", {
          project: projectName(
            { name: target.project_name, name_fr: target.project_name_fr },
            locale,
          ),
        })}
      </p>
      <div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={!prices || pending || busy}
          onClick={ask}
        >
          {pending || busy ? <Loader2 className="animate-spin" /> : <Users />}
          {pending
            ? translate("resources.projects.contacts.team.pending")
            : translate("resources.projects.contacts.team.button", {
                smart_count: price,
              })}
        </Button>
      </div>
      {latest && !pending && (
        <span className="text-xs text-muted-foreground">
          {translate("resources.projects.contacts.team.result", {
            date: formatDay(latest.processed_at, locale),
            smart_count: latest.found ?? 0,
          })}
          {latest.note && ` ${translateWorkerNote(latest.note, translate)}`}
        </span>
      )}
    </div>
  );
};
